// Shared formatting helpers for Okoshi note output.
//
// Two markdown builders:
//   - buildMarkdownForVault   — BODY ONLY (frontmatter is injected by
//     VaultRPC.writeNote on the Vault MCP side; adding it here would
//     double up the `---` block).
//   - buildMarkdownForDownload — frontmatter + body (file stands alone
//     when downloaded outside Koe, so we include the metadata).

/** Format a Date (or epoch ms) as `YYYY-MM-DD-HHMM` in JST via UTC+9 maths. */
export function formatJstStamp(when: Date | number = Date.now()): string {
  const d = typeof when === "number" ? new Date(when) : when;
  const jst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const yyyy = jst.getUTCFullYear();
  const mm = String(jst.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(jst.getUTCDate()).padStart(2, "0");
  const hh = String(jst.getUTCHours()).padStart(2, "0");
  const mi = String(jst.getUTCMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}-${hh}${mi}`;
}

/** Split a `YYYY-MM-DD-HHMM` stamp into the pieces used in frontmatter + title. */
export function splitStamp(stamp: string) {
  return {
    date: stamp.slice(0, 10), // YYYY-MM-DD
    hhmm: `${stamp.slice(11, 13)}:${stamp.slice(13, 15)}`, // HH:MM
  };
}

export interface TranscriptMarkdownMeta {
  title: string;
  stamp: string;
  /** ISO-8601 timestamp that the backend will parse + JST-format itself. */
  dateTimeIso: string;
}

function buildBody(finalTranscript: string, when: Date): {
  body: string;
  meta: TranscriptMarkdownMeta;
} {
  const stamp = formatJstStamp(when);
  const { date, hhmm } = splitStamp(stamp);
  const title = `文字起こし ${date} ${hhmm}`;
  const text = finalTranscript.trim() || "(空)";
  const body = `# ${title}\n\n${text}\n`;
  return {
    body,
    meta: { title, stamp, dateTimeIso: when.toISOString() },
  };
}

/** Body-only Markdown for `/api/vault/save`. VaultRPC prepends its own
 *  frontmatter (type/date/source). */
export function buildMarkdownForVault(
  finalTranscript: string,
  when: Date = new Date(),
): { markdown: string; meta: TranscriptMarkdownMeta } {
  const { body, meta } = buildBody(finalTranscript, when);
  return { markdown: body, meta };
}

/** Self-contained Markdown (frontmatter + body) for the "DL · .md" action. */
export function buildMarkdownForDownload(
  finalTranscript: string,
  when: Date = new Date(),
): { markdown: string; meta: TranscriptMarkdownMeta } {
  const { body, meta } = buildBody(finalTranscript, when);
  const { date } = splitStamp(meta.stamp);
  const markdown =
    `---\ntype: transcript\ndate: ${date}\nsource: koe\n---\n\n${body}`;
  return { markdown, meta };
}

/** For the "NEXT" path preview in the idle screen. */
export function nextOkoshiPathPreview(now: Date = new Date()): string {
  return `inbox/okoshi/${formatJstStamp(now)}.md`;
}
